import { Box, Modal } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames/bind'

import STORAGE_KEYS from '../../constants/localStorage'
import { selectPopupTopPage, showPopupTopPage } from '~/redux/popupTopPageSlice'
import LanguageSelection from './LanguageSelection'
import FormRoomHotel from './FormRoomHotel'
import { selectRestaurant } from '~/redux/restaurantSlice'
import { useThemeContext } from '~/theme/ThemeProvider'
import { darkThemeArray } from '~/theme/constants'
import styles from './styles/PopUpTopPage.module.css'
import { EXTERNAL_PARTNER, MERCHANT_TYPE } from '~/constants'
import NumberOfPeopleSelection from './NumberOfPeopleSelection'
import { KEY_PAGE_POPUP } from '~/constants/popupTopPage'
import { useAppContext } from '~/contexts/AppContext'

const cx = classNames.bind(styles)

// Hiển thị Popup ban đầu quét qr, bao gồm LanguageSelection, FormRoomHotel, Form chọn số lượng người
export default function PopUpTopPage() {
  const { currentThemeObject } = useThemeContext()
  const { isOpenPopupAddFriendLine } = useAppContext()
  const { open } = useSelector(selectPopupTopPage)
  const {
    tableUid,
    externalPartner,
    number_of_people,
    merchant_type,
    restaurantId,
    line_page_url,
    allow_order,
  } = useSelector(selectRestaurant)

  // Key dành cho popup add friend line
  const popupKey = `${STORAGE_KEYS.ADD_FRIEND_LINE}_${restaurantId}`
  const hasBeenOpened = localStorage.getItem(popupKey) === 'true'
  const locale = localStorage.getItem(STORAGE_KEYS.LOCALE)
  const numberLocal = localStorage.getItem(STORAGE_KEYS.NUMBER_OF_PEOPLE)
  const [keyPage, setKeyPage] = useState(KEY_PAGE_POPUP.LANGUAGE) // Biến sử dụng để chuyển trang
  const isTypeHotel = merchant_type === MERCHANT_TYPE.HOTEL

  const dispatch = useDispatch()

  // 1. Trường hợp chưa chọn ngôn ngữ
  useEffect(() => {
    if (!locale) {
      dispatch(showPopupTopPage())
    }
  }, [dispatch, isTypeHotel, locale, merchant_type, tableUid])

  /**
   * 2. Chỉ áp dụng đối với trường hợp là hotel
   *  Nếu trong trường hợp hotel và chưa nhập thông tin phòng(table uuid) thì di chuyển đến popup hotel
   */
  useEffect(() => {
    if (locale && isTypeHotel && !tableUid) {
      dispatch(showPopupTopPage())
      setKeyPage(KEY_PAGE_POPUP.HOTEL) // Di chuyển đến popup hotel
    }
  }, [dispatch, isTypeHotel, locale, merchant_type, tableUid])

  /**
   * 3. Chỉ áp dụng với exterbal_partner là Smaregi
   * trường hợp đã chọn ngôn ngữ và chưa chọn số lượng người ăn -> mở popup chọn số lượng người
   */
  useEffect(() => {
    // Nếu trường hợp có line friend nhưng chưa mở popup add line thì sẽ không hiển thị popup này.
    if (line_page_url && !hasBeenOpened) return

    if (
      locale &&
      externalPartner === EXTERNAL_PARTNER.Smaregi &&
      allow_order &&
      !number_of_people &&
      !numberLocal
    ) {
      dispatch(showPopupTopPage())
      setKeyPage(KEY_PAGE_POPUP.SMAREGI)
    }
  }, [
    dispatch,
    externalPartner,
    locale,
    numberLocal,
    number_of_people,
    tableUid,
    line_page_url,
    hasBeenOpened,
    isOpenPopupAddFriendLine,
    allow_order,
  ])

  const renderLanguageSelection = () => {
    /**
     *  1. Nếu trong trường hợp không phải màn hình chọn language
     *  2. Hoặc trong trường hợp trước đó đã nhập ngôn ngữ rồi và chưa nhập thông tin hotel
     *  => Thì sẽ không hiển thị popup này
     */
    if (keyPage !== KEY_PAGE_POPUP.LANGUAGE || (locale && isTypeHotel && !tableUid)) return null

    return <LanguageSelection setKeyPage={setKeyPage} />
  }

  // Trong trường hợp đã nhập form hotel -> có table uuid rồi thì không hiển thị form này nữa
  const renderFormRoomHotel = () => {
    if (keyPage !== KEY_PAGE_POPUP.HOTEL || (isTypeHotel && tableUid)) return null

    return <FormRoomHotel setKeyPage={setKeyPage} />
  }

  // Hiển thị form chọn số người trong trường hợp smaregi
  const renderNumberOfPeopleSelection = () => {
    if (
      keyPage !== KEY_PAGE_POPUP.SMAREGI ||
      number_of_people ||
      numberLocal || // Trường hợp đã nhập số người rồi thì cũng không hiển thị
      externalPartner !== EXTERNAL_PARTNER.Smaregi // Nếu không phải external partner là smaregi thì không hiển thị
    )
      return null

    return <NumberOfPeopleSelection setKeyPage={setKeyPage} />
  }

  // Đối với những theme tối thì hiển thị border color
  const borderPopup = useMemo(() => {
    return darkThemeArray.includes(currentThemeObject?.value) ? '2px solid #fff' : undefined
  }, [currentThemeObject?.value])

  return (
    <Modal open={open}>
      <Box
        border={borderPopup}
        className={cx('box_container')}
        bgcolor={keyPage === KEY_PAGE_POPUP.SMAREGI ? '#fff' : 'baseColor.color1'}>
        {renderLanguageSelection()}
        {renderFormRoomHotel()}
        {renderNumberOfPeopleSelection()}
      </Box>
    </Modal>
  )
}
